// LinkedInPreview — renderiza o output do editor exatamente como ficaria no feed do LinkedIn.
//
// Comportamentos:
//  - Mostra TEXTO INTEIRO por default (mais útil pra edição/revisão)
//  - Marca visualmente onde o LinkedIn cortaria com "...ver mais" via linha tracejada
//  - Hashtags ficam azuis, URLs idem
//  - Mídia anexada renderizada como grid
//  - Se draftId + selectedImageId fornecidos, renderiza imagem gerada abaixo do texto

import { splitAtLinkedInCutoff } from "../lib/linkedin-format";
import { useImagePreviewUrl } from "../hooks/useImagePreviewUrl";
import type { ImageCandidate } from "../lib/api";

interface LinkedInPreviewProps {
  text: string;
  authorName?: string;
  authorHeadline?: string;
  imageUrls?: string[];
  draftId?: string;
  selectedImageId?: string;
  imageCandidates?: ImageCandidate[];
}

export const LinkedInPreview = ({
  text,
  authorName = "Jean Philip de Rogatis",
  authorHeadline = "CTO | Technology Executive",
  imageUrls = [],
  draftId,
  selectedImageId,
  imageCandidates,
}: LinkedInPreviewProps) => {
  const { before: beforeCutoff, after: afterCutoff, truncated } = splitAtLinkedInCutoff(text);

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden max-w-[552px] mx-auto shadow-sm">
      {/* Header autor */}
      <div className="flex items-start gap-3 px-4 pt-3">
        <div className="w-12 h-12 rounded-full bg-slate-300 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-slate-900 truncate">{authorName}</div>
          <div className="text-xs text-slate-600 truncate">{authorHeadline}</div>
          <div className="text-xs text-slate-500 mt-0.5">Agora • 🌐</div>
        </div>
      </div>

      {/* Texto sempre completo, com marcador onde fica o cutoff */}
      <div className="px-4 pt-2 pb-3 text-sm text-slate-900 whitespace-pre-wrap break-words">
        <RichTextWithLinks text={beforeCutoff} />
        {truncated && (
          <>
            <div className="my-2 flex items-center gap-2 select-none">
              <div className="flex-1 border-t border-dashed border-slate-300" />
              <span className="text-xs text-slate-400 italic">↑ "...ver mais" cai aqui no feed</span>
              <div className="flex-1 border-t border-dashed border-slate-300" />
            </div>
            <RichTextWithLinks text={afterCutoff} />
          </>
        )}
      </div>

      {/* Imagem gerada selecionada (F2.5) */}
      {draftId && selectedImageId && (
        <SelectedPreviewImage
          draftId={draftId}
          selectedImageId={selectedImageId}
          candidates={imageCandidates}
        />
      )}

      {/* Imagens estáticas (legado / fallback) */}
      {imageUrls.length > 0 && (
        <div className={`grid gap-px bg-slate-200 ${imageUrls.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
          {imageUrls.slice(0, 4).map((url, i) => (
            <img key={i} src={url} alt="" className="w-full object-cover max-h-[400px] bg-slate-100" />
          ))}
        </div>
      )}

      {/* Engagement bar fake */}
      <div className="border-t border-slate-200 px-4 py-2 flex items-center gap-4 text-xs text-slate-600">
        <span>👍 Curtir</span>
        <span>💬 Comentar</span>
        <span>🔁 Compartilhar</span>
        <span>📨 Enviar</span>
      </div>
    </div>
  );
};

// Extrai candidate pelo id e delega pra PreviewImage — elimina IIFE no JSX
interface SelectedPreviewImageProps {
  draftId: string;
  selectedImageId: string;
  candidates?: ImageCandidate[];
}

const SelectedPreviewImage = ({ draftId, selectedImageId, candidates }: SelectedPreviewImageProps) => {
  const candidate = candidates?.find((c) => c.id === selectedImageId);
  if (!candidate) return null;
  return <PreviewImage draftId={draftId} candidate={candidate} />;
};

// Renderiza imagem selecionada com presigned URL — carrega via useImagePreviewUrl
interface PreviewImageProps {
  draftId: string;
  candidate: ImageCandidate;
}

const PreviewImage = ({ draftId, candidate }: PreviewImageProps) => {
  const { data } = useImagePreviewUrl(draftId, candidate.id);
  if (!data?.url) return <div className="aspect-square w-full animate-pulse bg-slate-100" />;
  return (
    <img
      src={data.url}
      alt={candidate.prompt.slice(0, 80)}
      className="aspect-square w-full object-cover"
      loading="lazy"
    />
  );
};

// Detecta hashtags, mentions e URLs e renderiza com cor azul (como o LinkedIn faz)
const RichTextWithLinks = ({ text }: { text: string }) => {
  const PATTERN = /(#[\p{L}\p{N}_]+|@[\p{L}\p{N}_]+|https?:\/\/\S+)/gu;
  const parts = text.split(PATTERN);

  return (
    <>
      {parts.map((part, i) => {
        if (!part) return null;
        if (/^#|^@/.test(part) || /^https?:\/\//.test(part)) {
          return (
            <span key={i} className="text-[#0a66c2] hover:underline cursor-pointer">
              {part}
            </span>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
};
